import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '../api/axiosManager';
import toast from 'react-hot-toast';

// Login
export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const response = await api.post('/api/admin/auth/login', { email, password });
      
      if (response.data.success && response.data.data) {
        const { user, tokens } = response.data.data;
        
        toast.success('Giriş başarılı');
        return { user, tokens };
      }
      
      return rejectWithValue(response.data.message || 'Giriş başarısız');
    } catch (error) {
      // Timeout veya network hatası için özel mesaj
      let errorMsg = 'Giriş başarısız';
      
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMsg = 'Sunucu yanıt vermiyor. Lütfen daha sonra tekrar deneyin.';
      } else if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
        errorMsg = 'Sunucuya erişilemiyor. Lütfen internet bağlantınızı kontrol edin.';
      } else if (error.userMessage) {
        errorMsg = error.userMessage;
      } else if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      toast.error(errorMsg);
      return rejectWithValue(errorMsg);
    }
  }
);

// Logout
export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      // Backend'e logout isteği gönder
        try {
        await api.post('/api/admin/auth/logout');
        } catch (error) {
        // Logout hatası önemli değil
        }
      
      toast.success('Çıkış yapıldı');
      return null;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Çıkış başarısız');
    }
  }
);

// Profile Getir
export const fetchProfile = createAsyncThunk(
  'auth/fetchProfile',
  async (options = {}, { rejectWithValue, getState }) => {
    // options: { silent: boolean } - silent: true ise loading state'ini güncelleme
    const silent = options?.silent || false;
    try {
      const response = await api.get('/api/admin/auth/profile');
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      
      return rejectWithValue('Profil bilgisi alınamadı');
    } catch (error) {
      // 500 hatası = Server hatası, geçici olabilir
      // Mevcut user bilgisini koru (localStorage'dan veya state'ten)
      if (error.response?.status === 500) {
        // Mevcut user bilgisini localStorage'dan al
        const savedUser = localStorage.getItem('userData');
        if (savedUser) {
          try {
            const userData = JSON.parse(savedUser);
            // Mevcut user bilgisini döndür (permission'lar korunur)
            return userData;
          } catch (parseError) {
            // Hata sessizce yok sayılıyor
          }
        }
        
        // State'ten user bilgisini al
        const state = getState();
        if (state?.auth?.user) {
          return state.auth.user;
        }
      }
      
      return rejectWithValue(error.response?.data?.message || 'Profil bilgisi alınamadı');
    }
  }
);

// Initial State
const initialState = {
  user: null,
  tokens: null,
  isAuthenticated: false,
  loading: false, // Login/logout için kullanılır
  profileLoading: false, // fetchProfile için ayrı loading state (token yenileme sırasında kullanılır)
  error: null,
};

// Auth Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setUser: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
    },
    // Sayfa yüklendiğinde token kontrolü
    checkAuth: (state) => {
        // User bilgisini localStorage'dan oku (sadece state'te user yoksa)
        // Bu sonsuz döngüyü önlemek için önemli
        if (!state.user) {
          const savedUser = localStorage.getItem('userData');
          if (savedUser) {
            try {
              state.user = JSON.parse(savedUser);
            state.isAuthenticated = true;
            } catch (error) {
              localStorage.removeItem('userData');
            state.isAuthenticated = false;
            state.user = null;
          }
        } else {
          state.isAuthenticated = false;
          state.user = null;
        }
      } else {
        state.isAuthenticated = true;
      }
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.tokens = action.payload.tokens;
        state.isAuthenticated = true;
        state.error = null;
        // User bilgisini localStorage'a kaydet
        if (action.payload.user) {
          localStorage.setItem('userData', JSON.stringify(action.payload.user));
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
        state.user = null;
        state.tokens = null;
      });

    // Logout
    builder
      .addCase(logout.pending, (state) => {
        state.loading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.loading = false;
        state.user = null;
        state.tokens = null;
        state.isAuthenticated = false;
        state.error = null;
        // User bilgisini localStorage'dan temizle
        localStorage.removeItem('userData');
      })
      .addCase(logout.rejected, (state, action) => {
        state.loading = false;
        state.user = null;
        state.tokens = null;
        state.isAuthenticated = false;
        state.error = action.payload;
      });


    // Fetch Profile
    builder
      .addCase(fetchProfile.pending, (state, action) => {
        // Silent mod kontrolü - token yenileme sırasında loading state'ini güncelleme
        const silent = action.meta?.arg?.silent || false;
        if (!silent) {
          state.loading = true;
        }
        state.profileLoading = true;
      })
      .addCase(fetchProfile.fulfilled, (state, action) => {
        const silent = action.meta?.arg?.silent || false;
        if (!silent) {
          state.loading = false;
        }
        state.profileLoading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.error = null;
        // User bilgisini localStorage'a kaydet
        if (action.payload) {
          localStorage.setItem('userData', JSON.stringify(action.payload));
        }
      })
      .addCase(fetchProfile.rejected, (state, action) => {
        const silent = action.meta?.arg?.silent || false;
        if (!silent) {
          state.loading = false;
        }
        state.profileLoading = false;
        state.error = action.payload;
        
        // Hata durumunu kontrol et
        const errorStatus = action.payload?.response?.status || action.payload?.status;
        
        if (errorStatus === 401) {
          // 401 hatası: Unauthorized, logout yap
            localStorage.removeItem('userData');
            state.isAuthenticated = false;
            state.user = null;
            state.tokens = null;
        } else if (errorStatus === 500 || errorStatus >= 500) {
          // 500+ hatası: Server hatası, user bilgisini koru
          // User bilgisi varsa authenticated kal
          if (state.user) {
            state.isAuthenticated = true;
          }
        } else {
          // Diğer hatalar (400, 403, vb.) için user varsa authenticated kal
          if (state.user) {
            state.isAuthenticated = true;
          }
        }
      });
  },
});

export const { clearError, setUser, checkAuth } = authSlice.actions;

export default authSlice.reducer;

